<template>
  <div class="infrastructure">
    <div class="infrastructure__links-shadow-top-blocker"></div>
    <div class="infrastructure__links" ref="links">
      <HotelLinks 
        :links="links" 
        :selected-link="selectedHashTag" 
        @click="onClickLinkHandler"
      />
    </div>
    <div class="infrastructure__links-shadow-bottom-blocker" :style="{top: linksShadowBlockerTop}"></div>
    <div class="infrastructure__block" v-for="group in featureGroups" :key="group.name">
      <div :id="group.hashtag" class="infrastructure__header headings-font-family">
        {{ group.name }}
      </div>
      <div class="infrastructure__content">
        <div v-for="feature in group.features" class="infrastructure__row">
          <div class="infrastructure__title">{{ feature.name }}</div>
          <div class="infrastructure__value" v-html="getServiceStatus(feature.status)"></div>
        </div>
        <div v-for="remark in group.remarks" class="infrastructure__row">
          <div class="infrastructure__title">{{ remark.title }}</div>
          <div class="infrastructure__value" v-html="remark.text"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {HotelDetailsHelper} from "../helpers";
import HotelLinks from "../links/HotelLinksComponent.vue";
import SvgIcon from "@/common/controls/SvgIconControl.vue";

export default {
  components: {
    SvgIcon,
    HotelLinks
  },
  props: {
    viewModel: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      selectedHashTag: '',
      linksShadowBlockerTop: '60px',
      ignoreObserver: false
    }
  },
  mounted() {
    if (this.$refs.links) {
      this.linksShadowBlockerTop = `${this.$refs.links?.clientHeight}px`;
      window.addEventListener('scroll', this.onScrollHandler, { passive: true });
      this.$nextTick(() => {
        this.onScrollHandler();
      });
    }
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.onScrollHandler);
  },
  methods: {
    getBlocks() {
      return [...this.$el.querySelectorAll('.infrastructure__block')];
    },
    updateSelectedFromViewport() {
      const blocks = this.getBlocks();

      if (!blocks.length) {
        return;
      }

      const anchorLine = 108;
      const blockAtAnchor = blocks.find((block) => {
        const { top, bottom } = block.getBoundingClientRect();
        return top <= anchorLine && bottom > anchorLine;
      });

      const nextVisibleBlock = blocks.find((block) => {
        return block.getBoundingClientRect().top > anchorLine;
      });

      const activeBlock = blockAtAnchor || nextVisibleBlock || blocks[blocks.length - 1];
      const header = activeBlock?.querySelector('.infrastructure__header');
      const id = header?.id;

      if (id) {
        this.selectedHashTag = `#${id}`;
      }
    },
    onScrollHandler() {
      if (this.ignoreObserver) {
        return;
      }
      
      this.updateSelectedFromViewport();
    },
    onClickLinkHandler(hashTag) {
      if (typeof hashTag !== "string") {
        return;
      }

      this.selectedHashTag = hashTag;
      const element = this.$el.querySelector(hashTag);

      if (element) {
        this.scrollToTargetAdjusted(element);
      }
    },
    scrollToTargetAdjusted(element) {
      const headerOffset = this.$refs.links.getBoundingClientRect().height;
      const elementPosition = element.getBoundingClientRect().top;
      const gapOffset = 8;
      
      const offsetPosition = elementPosition + window.scrollY - headerOffset - gapOffset;

      this.ignoreObserver = true;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });

      setTimeout(() => {
        this.ignoreObserver = false;
        this.updateSelectedFromViewport();
      }, 1000);
    },
    getServiceStatus(statusCode) {
      return HotelDetailsHelper.getFeatureStatus.call(this, statusCode);
    },
    getServiceStatusList(statusCodes) {
      return statusCodes.join(", ");
    }
  },
  computed: {
    featureGroups() {
      return this.viewModel.hotelDescription?.features?.featureGroups.map((group, ix) => {
        return {...group, hashtag: 'group'+ix}
      }) || [];
    },    
    links() {
      return this.featureGroups.map(group => ({name: group.name, hashtag: `#${group.hashtag}`}));
    }
  }
}
</script>

<style lang="scss" scoped>


.infrastructure {
  position: relative;
  margin-top: -19px;

  &__links {
    position: sticky;
    top: 0;
    background-color: var(--white);
    padding: 10px 25px;
    margin-left: -24px;
    margin-right: -24px;
    box-shadow: var(--shadow);
    border-radius: 0 0 var(--border-radius-lg) var(--border-radius-lg);
  }

  &__links-shadow-bottom-blocker {
    position: sticky;
    top: 60px;
    height: 11px;
    min-width: 100%;
    background-color: #fff;
    margin-left: -24px;
    margin-right: -24px;
  }

  &__links-shadow-top-blocker {
    position: sticky;
    top: 0;
    height: 10px;
    min-width: 100%;
    background-color: #fff;
    margin-left: -24px;
    margin-right: -24px;
    z-index: 1;
  }

  &__block {
    box-shadow: var(--shadow);
    border-radius: var(--border-radius-md);
  }
  &__block:not(:last-of-type) {
    margin-bottom: 20px;
  }

  &__header {
    background: var(--main-50);
    padding: 15px 24px;
    border-radius: var(--border-radius-md) var(--border-radius-md) 0 0;
    font-weight: 600;
    font-size: 18px;
    line-height: 28px;

    svg {
      margin-right: 5px;
    }
  }

  &__content {
    border: 1px solid var(--black-5);
    border-radius: 0 0 var(--border-radius-md) var(--border-radius-md);
  }

  &__row {
    display: flex;
    align-items: baseline;
    gap: 12px;
    padding: 20px 24px;
  }

  &__row:not(:last-of-type) {
    border-bottom: 2px solid var(--main-50);
  }

  &__title {
    font-weight: 600;
    font-size: 13px;
    line-height: 20px;
    width: 249px;
    flex-shrink: 0;
  }

  :deep(.infrastructure__value--danger) {
    color: var(--system-10);
  }
}
</style>
